/**
 * Every address of the interface, built in one place.
 *
 * The interface is a set of pages a person can open in a new tab, bookmark,
 * reload and send to a colleague: the selection lives in the URL, never only
 * in component state. Screens never concatenate paths by hand — they ask
 * `paths` — so renaming a slug is one edit here and one entry in
 * `legacy-hash.ts`, not a hunt through thirty components.
 *
 * Pure and framework-free: `routes.test.ts` exercises it without React.
 */

const enc = encodeURIComponent

type QueryValue = string | number | undefined | null

function withQuery(path: string, query: Record<string, QueryValue>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const search = params.toString()
  return search ? `${path}?${search}` : path
}

/** The sections of a site, in drawer order. The slug is the URL segment. */
export const SITE_SECTIONS = [
  'contenuti',
  'media',
  'calendario',
  'piano',
  'pagine',
  'menu',
  'layout',
  'elenchi',
  'redirect',
  'tema',
  'sezioni',
  'pubblicazioni',
  'impostazioni',
] as const
export type SiteSection = (typeof SITE_SECTIONS)[number]

export interface MediaFilters {
  q?: string
  /** image, document, video; absent means every kind. */
  kind?: string
  /** active is the default and stays out of the URL. */
  status?: string
  page?: number
}

export type ScheduleView = 'settimana' | 'elenco'

export interface PlanFilters {
  /** proposal, approved, writing, review, published or discarded; absent means every status. */
  status?: string
  q?: string
  /** The board is the default and stays out of the URL. */
  view?: 'bacheca' | 'elenco'
  /** A dialog opened over the plan: import from the SEO tool or pasted JSON, a new item, the SEO bridge settings. */
  import?: 'seo' | 'json'
  dialog?: 'nuova' | 'ponte'
}

export interface RedirectFilters {
  q?: string
  page?: number
  size?: number
}

export interface PageListFilters {
  /** draft, review, published or trashed; absent means everything except the trash. */
  status?: string
  q?: string
  view?: 'albero' | 'elenco'
}

export type ContentTab = 'contenuto' | 'seo' | 'strumenti'
export const CONTENT_TABS: readonly ContentTab[] = ['contenuto', 'seo', 'strumenti']

export interface ContentListFilters {
  type?: string
  status?: string
  q?: string
  /** 1-based; page 1 is left out of the URL. */
  page?: number
  size?: number
}

const site = (siteId: string) => `/s/${enc(siteId)}`

export const paths = {
  home: () => '/',
  login: (returnTo?: string) => withQuery('/accesso', { ritorno: returnTo && returnTo !== '/' ? returnTo : undefined }),

  site: (siteId: string) => site(siteId),
  siteSection: (siteId: string, section: SiteSection) => `${site(siteId)}/${section}`,

  contents: (siteId: string, filters: ContentListFilters = {}) => withQuery(`${site(siteId)}/contenuti`, {
    type: filters.type,
    status: filters.status,
    q: filters.q,
    page: filters.page && filters.page > 1 ? filters.page : undefined,
    size: filters.size,
  }),
  newContent: (siteId: string, type?: string) => withQuery(`${site(siteId)}/contenuti/nuovo`, { type }),
  content: (siteId: string, id: string, options: { tab?: ContentTab; upTo?: number } = {}) =>
    withQuery(`${site(siteId)}/contenuti/${enc(id)}`, {
      tab: options.tab === 'contenuto' ? undefined : options.tab,
      upTo: options.upTo,
    }),
  contentPage: (siteId: string, id: string) => `${site(siteId)}/contenuti/${enc(id)}/pagina`,
  contentHistory: (siteId: string, id: string) => `${site(siteId)}/contenuti/${enc(id)}/storia`,
  /** A content whose site is not known yet (global search, old links): the screen finds the site and redirects. */
  contentAnySite: (id: string, view?: 'pagina' | 'storia', upTo?: number) =>
    withQuery(`/contenuti/${enc(id)}${view ? `/${view}` : ''}`, { upTo }),

  media: (siteId: string, filters: MediaFilters = {}) => withQuery(`${site(siteId)}/media`, {
    q: filters.q,
    kind: filters.kind,
    stato: filters.status === 'active' ? undefined : filters.status,
    page: filters.page && filters.page > 1 ? filters.page : undefined,
  }),
  /** The detail opens over the library, with the library's filters kept behind it. */
  mediaAsset: (siteId: string, assetId: string, filters: MediaFilters = {}) => withQuery(`${site(siteId)}/media/${enc(assetId)}`, {
    q: filters.q,
    kind: filters.kind,
    stato: filters.status === 'active' ? undefined : filters.status,
    page: filters.page && filters.page > 1 ? filters.page : undefined,
  }),

  /** `data` is any day of the week shown (`YYYY-MM-DD`); absent means this week. */
  schedule: (siteId: string, options: { view?: ScheduleView; day?: string } = {}) => withQuery(`${site(siteId)}/calendario`, {
    vista: options.view === 'settimana' ? undefined : options.view,
    data: options.day,
  }),

  /** The editorial plan (document/63): board or list, filters and dialogs live in the URL. */
  plan: (siteId: string, filters: PlanFilters = {}) => withQuery(`${site(siteId)}/piano`, {
    stato: filters.status,
    q: filters.q,
    vista: filters.view === 'elenco' ? 'elenco' : undefined,
    importa: filters.import,
    nuova: filters.dialog === 'nuova' ? 1 : undefined,
    ponte: filters.dialog === 'ponte' ? 1 : undefined,
  }),
  planItem: (siteId: string, itemId: string) => `${site(siteId)}/piano/${enc(itemId)}`,

  redirects: (siteId: string, filters: RedirectFilters = {}) => withQuery(`${site(siteId)}/redirect`, {
    q: filters.q,
    page: filters.page && filters.page > 1 ? filters.page : undefined,
    size: filters.size,
  }),

  pages: (siteId: string, filters: PageListFilters = {}) => withQuery(`${site(siteId)}/pagine`, {
    status: filters.status,
    q: filters.q,
    // The tree is the default view and stays out of the URL.
    vista: filters.view === 'elenco' ? 'elenco' : undefined,
  }),
  /** The "new page" dialog over the list; `parentId` pre-selects the page it hangs under. */
  newPage: (siteId: string, parentId?: string) => withQuery(`${site(siteId)}/pagine`, { nuova: 1, genitore: parentId }),
  /** A page's detail: title, SEO, address and lifecycle. */
  page: (siteId: string, pageId: string) => `${site(siteId)}/pagine/${enc(pageId)}`,
  /** The section composer of a page, with the selected section in the URL. */
  pageSections: (siteId: string, pageId: string, sectionId?: string) =>
    withQuery(`${site(siteId)}/pagine/${enc(pageId)}/sezioni`, { sezione: sectionId }),
  pageHistory: (siteId: string, pageId: string) => `${site(siteId)}/pagine/${enc(pageId)}/storia`,

  menus: (siteId: string) => `${site(siteId)}/menu`,
  menu: (siteId: string, key: string) => `${site(siteId)}/menu/${enc(key)}`,

  layouts: (siteId: string, options: { trash?: boolean } = {}) =>
    withQuery(`${site(siteId)}/layout`, { cestino: options.trash ? 1 : undefined }),
  layout: (siteId: string, key: string) => `${site(siteId)}/layout/${enc(key)}`,
  newLayout: (siteId: string) => withQuery(`${site(siteId)}/layout`, { nuovo: 1 }),

  listings: (siteId: string) => `${site(siteId)}/elenchi`,
  listing: (siteId: string, key: string) => `${site(siteId)}/elenchi/${enc(key)}`,
  newListing: (siteId: string) => withQuery(`${site(siteId)}/elenchi`, { nuovo: 1 }),

  theme: (siteId: string, options: { themeId?: string; locale?: string } = {}) =>
    withQuery(`${site(siteId)}/tema`, { tema: options.themeId, lingua: options.locale }),
  sections: (siteId: string, key?: string) => `${site(siteId)}/sezioni${key ? `/${enc(key)}` : ''}`,
  publications: (siteId: string, status?: string) => withQuery(`${site(siteId)}/pubblicazioni`, { status }),
  settings: (siteId: string) => `${site(siteId)}/impostazioni`,
  /** Where the site publishes: the Pages project and its domains (document/61 M2, M3). */
  sitePublishing: (siteId: string) => `${site(siteId)}/impostazioni/pubblicazione`,

  people: () => '/persone',
  person: (id: string, projectId?: string) => withQuery(`/persone/${enc(id)}`, { progetto: projectId }),
  invitations: (status?: string) => withQuery('/persone/inviti', { stato: status }),
  accessPreview: () => '/persone/accesso',
  apiKeys: () => '/chiavi-api',
  operator: () => '/operatore',
  newTenant: () => '/operatore/nuovo-cliente',
  knowledge: () => '/conoscenza',
  newSite: () => '/siti/nuovo',
  releaseNotes: () => '/note-di-rilascio',
}

/**
 * Where a person may be sent back to after the login: a path on this origin
 * and nothing else. `//evil.test` and `/\evil.test` are both read by browsers
 * as another host, so they fall back to the home like any full URL does.
 */
export function safeReturnPath(raw: string | null | undefined): string {
  if (!raw) return '/'
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return '/'
  if (/[\u0000-\u001f\\]/.test(raw)) return '/'
  if (raw === '/accesso' || raw.startsWith('/accesso?') || raw.startsWith('/accesso/')) return '/'
  return raw
}

/**
 * The same place on another site: from `/s/A/contenuti/123?tab=seo` to
 * `/s/B/contenuti`. Ids belong to the site they came from, so only the
 * section survives the switch.
 */
export function switchSitePath(pathname: string, siteId: string): string {
  const match = /^\/s\/[^/]+\/([^/?#]+)/.exec(pathname)
  const section = match?.[1]
  if (section && (SITE_SECTIONS as readonly string[]).includes(section)) {
    return paths.siteSection(siteId, section as SiteSection)
  }
  return paths.site(siteId)
}

/** The section a site path belongs to, for the drawer's active item; `null` on the overview. */
export function siteSectionOf(pathname: string): SiteSection | null {
  const match = /^\/s\/[^/]+\/([^/?#]+)/.exec(pathname)
  const section = match?.[1]
  return section && (SITE_SECTIONS as readonly string[]).includes(section) ? section as SiteSection : null
}

export function isContentTab(value: string | null | undefined): value is ContentTab {
  return !!value && (CONTENT_TABS as readonly string[]).includes(value)
}
